
import { useState, useRef } from 'react';
import { FileText, Upload, X, CheckCircle, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

interface ResumeUploaderProps {
  onFileUpload: (file: File) => void;
}

const ResumeUploader = ({ onFileUpload }: ResumeUploaderProps) => {
  const [file, setFile] = useState<File | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      const droppedFile = e.dataTransfer.files[0];
      validateAndSetFile(droppedFile);
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      validateAndSetFile(e.target.files[0]);
    }
  };

  const validateAndSetFile = (selectedFile: File) => {
    // Check file size (10MB limit)
    if (selectedFile.size > 10 * 1024 * 1024) {
      toast.error('File size exceeds 10MB limit');
      return;
    }

    // Check file type
    const allowedTypes = ['application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'];
    if (!allowedTypes.includes(selectedFile.type)) {
      toast.error('Only PDF and Word documents are allowed');
      return;
    }

    // Simulate upload process
    setIsUploading(true);
    setProgress(0);
    
    const interval = setInterval(() => {
      setProgress((prev) => {
        const newProgress = prev + 10;
        if (newProgress >= 100) {
          clearInterval(interval);
          setTimeout(() => {
            setFile(selectedFile);
            setIsUploading(false);
            onFileUpload(selectedFile);
            toast.success('Resume uploaded successfully');
          }, 500);
          return 100;
        }
        return newProgress;
      });
    }, 150);
  };

  const handleRemoveFile = () => {
    setFile(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const formatFileSize = (bytes: number) => {
    if (bytes < 1024) return bytes + ' B';
    else if (bytes < 1048576) return (bytes / 1024).toFixed(1) + ' KB';
    else return (bytes / 1048576).toFixed(1) + ' MB';
  };

  return (
    <div>
      {!file && !isUploading ? (
        <div
          className={`border-2 border-dashed rounded-xl p-6 text-center transition-all ${
            isDragging ? 'border-apple-blue bg-blue-50' : 'border-gray-300'
          }`}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
        >
          <div className="mx-auto w-14 h-14 rounded-full bg-gray-100 flex items-center justify-center mb-4">
            <FileText className="text-apple-text-secondary" size={24} />
          </div>
          <h3 className="text-lg font-medium text-apple-text mb-2">
            Drag & Drop Your Resume
          </h3>
          <p className="text-sm text-apple-text-secondary mb-4">
            Supported formats: PDF, DOC, DOCX (max 10MB)
          </p>
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            className="apple-btn-secondary inline-flex items-center"
          >
            <Upload size={18} className="mr-2" />
            Browse Files
          </button>
          <input
            type="file"
            ref={fileInputRef}
            className="hidden"
            accept=".pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
            onChange={handleFileChange}
          />
        </div>
      ) : isUploading ? (
        <div className="border rounded-xl p-6">
          <div className="flex items-center mb-3">
            <Loader2 className="animate-spin mr-3 text-apple-blue" size={20} />
            <span className="font-medium text-apple-text">Uploading Resume...</span>
          </div>
          <div className="h-2 bg-gray-200 rounded-full overflow-hidden mb-2">
            <div 
              className="h-full bg-apple-blue transition-all duration-300"
              style={{ width: `${progress}%` }}
            ></div>
          </div>
          <div className="text-xs text-apple-text-secondary">
            {progress}% complete
          </div>
        </div>
      ) : (
        <div className="border rounded-xl p-4 bg-gray-50">
          <div className="flex items-center">
            <div className="mr-3 p-2 bg-white rounded-lg">
              <FileText className="text-apple-blue" size={24} />
            </div>
            <div className="flex-grow overflow-hidden">
              <div className="font-medium text-apple-text truncate">
                {file?.name}
              </div>
              <div className="text-sm text-apple-text-secondary">
                {formatFileSize(file?.size || 0)}
              </div>
            </div>
            <div className="flex items-center space-x-2">
              <button
                type="button"
                onClick={handleRemoveFile}
                className="p-1.5 text-gray-500 hover:text-red-500 transition-colors rounded-full hover:bg-gray-100"
                aria-label="Remove file"
              >
                <X size={16} />
              </button>
              <div className="text-apple-blue">
                <CheckCircle size={20} />
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ResumeUploader;
